import type { ReactNode } from 'react';
import { Trans } from 'react-i18next';
import {
  DataSourcesLink,
  LegalLink,
  LegalLinks,
  LocaleSwitch,
  Logo,
  ThemeToggle,
} from '../../app/AppShell';

const FOOTER_LINK = 'text-slate-500 underline-offset-2 hover:underline dark:text-slate-400';

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col bg-slate-50 dark:bg-slate-950">
      <header className="flex items-center justify-between px-5 py-4">
        <Logo />
        <div className="flex items-center gap-1.5">
          <LocaleSwitch persist={false} />
          <ThemeToggle />
        </div>
      </header>
      <main id="main" className="flex flex-1 items-start justify-center px-4 pt-6 pb-16 sm:pt-12">
        <div className="w-full max-w-sm">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>
          )}
          <div className="card mt-5 p-5">{children}</div>
          {footer && (
            <div className="mt-4 text-center text-sm text-slate-600 dark:text-slate-400">
              {footer}
            </div>
          )}
          <p className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-center text-xs">
            <LegalLinks className={FOOTER_LINK} />
            <DataSourcesLink className={FOOTER_LINK} />
          </p>
        </div>
      </main>
    </div>
  );
}

/** Signing up or in (also with Google or GitHub) accepts the terms (§ legal pages). */
export function TermsNotice() {
  return (
    <p className="mt-4 text-xs text-slate-500 dark:text-slate-400" data-testid="terms-notice">
      <Trans
        i18nKey="auth.termsNotice"
        components={{
          terms: <LegalLink page="terms" className="link" />,
          privacy: <LegalLink page="privacy" className="link" />,
        }}
      />
    </p>
  );
}
